'use client';

import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useWorkspaceNavigate } from './useWorkspaceNavigate';

interface NavOptions {
  scroll?: boolean;
}

/**
 * Drop-in replacement for `next/navigation`'s `useRouter` inside the workspace
 * shell (spec: workspace-static-spa-routing).
 *
 * `push`/`replace` to an in-shell target (`/workspace/{tenant}/…`) use the
 * History API so navigation is a client-side view swap — no full reload and no
 * per-route RSC fetch (which doesn't exist under static export). Every other
 * target (`/main`, `/login`, `/welcome/…`, bare `/workspace`) falls through to
 * the real Next router. `back`/`forward`/`refresh`/`prefetch` pass through
 * unchanged.
 *
 * The History-API path additionally requires the WorkspaceShell to be MOUNTED
 * right now — it's the subscriber that re-resolves the view (and reconciles the
 * tenant) from the URL. When the caller is rendered OUTSIDE the shell (e.g. the
 * tenant picker opened on `/main`, or the settings page), a `pushState` would
 * move the address bar without ever mounting the shell, leaving a dead page. So
 * ENTERING the shell is always a real Next navigation; only switching tenants /
 * sub-pages while ALREADY in the shell is the smooth History-API swap. This is
 * what keeps an in-shell tenant switch from full-reloading (the regression this
 * guard fixes) while an out-of-shell switch correctly mounts the shell.
 *
 * Migration is mechanical: swap `const router = useRouter()` for
 * `const router = useWorkspaceRouter()` in components that navigate to
 * workspace routes; existing `router.push(...)` call-sites keep working.
 */
export function useWorkspaceRouter() {
  const router = useRouter();
  const navigate = useWorkspaceNavigate();

  return useMemo(() => {
    // In-shell target = a tenant-scoped workspace path. Bare `/workspace` is the
    // resolver and is left to the real router (it redirects on to a tenant).
    const isInShellTarget = (href: string): boolean => {
      const path = href.split('?')[0].split('#')[0];
      return path.startsWith('/workspace/');
    };

    // Is the WorkspaceShell currently mounted? Read live so it's correct at
    // click time regardless of where the component is rendered.
    const currentlyInShell = (): boolean => {
      if (typeof window === 'undefined') return false;
      const p = window.location.pathname.replace(/\/+$/, '');
      return p === '/workspace' || p.startsWith('/workspace/');
    };

    const shouldUseHistory = (href: string): boolean =>
      isInShellTarget(href) && currentlyInShell();

    return {
      ...router,
      push: (href: string, opts?: NavOptions) =>
        shouldUseHistory(href)
          ? navigate(href, { preserveScroll: opts?.scroll === false })
          : router.push(href, opts),
      replace: (href: string, opts?: NavOptions) =>
        shouldUseHistory(href)
          ? navigate(href, { replace: true, preserveScroll: opts?.scroll === false })
          : router.replace(href, opts),
    };
  }, [router, navigate]);
}
